import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DevTool } from '@hookform/devtools';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@casops/store/hooks';
import { DrawerForm, DrawerPanel, ErrorResponse, SuccessNotificationContext } from '@dmc/ui';
import { GeofenceStyleFormModel, useManageGeofenceStyle } from './useManageGeofenceStyle';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import styles from './ManageGeofenceStyle.module.scss';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import FormColourConfigSelector from '@casops/components/inputs/form-config-selectors/form-colour-config-selector/FormColourConfigSelector';
import FormBorderConfigSelector from '@casops/components/inputs/form-config-selectors/form-border-config-selector/FormBorderConfigSelector';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import { ValidNameRegex, hexColorRegex } from '@casops/store/apis/commonModels';
import CasDivider from '@casops/components/layout/cas-divider/CasDivider';
import { useFormEditMode } from '@casops/components/layout/model-form/useFormEditMode';

const modelName = ModelName.GeofenceStyle;

const DESCRIPTION_MAX = 255;

export function ManageGeofenceStyle(): ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const editMode = useFormEditMode();

  const { isLoaded, initialValues, submitCreate, submitPatch } = useManageGeofenceStyle();

  const schema = useMemo(() => {
    const colourField = z
      .string()
      .regex(hexColorRegex, { message: t('Validation.SpecifyValidColour') });
    const patternField = z
      .number()
      .int({ message: t('Validation.ValueMustBeInteger') })
      .min(0, { message: t('Validation.SpecifyValidPattern') });
    const opacityField = z
      .number()
      .int({ message: t('Validation.ValueMustBeInteger') })
      .min(0, { message: t('Validation.SpecifyValidOpacity') })
      .max(100, { message: t('Validation.SpecifyValidOpacity') });
    const widthField = z
      .number()
      .int({ message: t('Validation.ValueMustBeInteger') })
      .min(0, { message: t('Validation.SpecifyValidWidth') })
      .max(8, { message: t('Validation.SpecifyValidWidth') });

    return z.object({
      name: z
        .string()
        .min(1, { message: t('Validation.SpecifyStyleName') })
        .max(128, { message: t('Validation.StyleNameTooLong') })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
      line: z.object({
        colour: colourField,
        pattern: patternField.min(1),
        opacity: opacityField,
        width: widthField,
      }),

      fill: z.object({
        defaultColour: colourField,
        activeColour: colourField,
        pattern: patternField,
        opacity: opacityField,
      }),
    });
  }, [t]);

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting, isValid, dirtyFields },
    watch,
    setValue,
  } = useForm<GeofenceStyleFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const [errorMessage, setErrorMessage] = useState<string>();

  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const canSubmit = !isSubmitting && isAnyFieldDirty && isValid;

  const onSubmitInternal = useCallback(
    async (data: GeofenceStyleFormModel) => {
      try {
        // Clear any existing error.
        setErrorMessage(undefined);

        const { name, desc, fill, line } = data;
        const { id } = initialValues;

        if (editMode) {
          await submitPatch({ name, desc, id, fill, line });
        } else {
          await submitCreate({ name, desc, fill, line });
        }

        // If this point is reached, the create/edit action was successful, so show success message and close the side panel.
        showSuccessMessage(t(`Model.${editMode ? 'Edit' : 'Create'}.Success`, { modelName }));
        dispatch(closeSidePanel());
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        const nameError = errorResponse.getValidationError()?.getFieldError('Name');
        if (nameError !== undefined) {
          setError('name', { type: 'custom', message: nameError });
        }

        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [initialValues, editMode, showSuccessMessage, t, dispatch, submitPatch, submitCreate, setError],
  );

  return (
    <>
      <DrawerPanel
        isLoaded={isLoaded}
        header={t(`Model.${editMode ? 'Edit' : 'Create'}.Title`, { modelName })}
      >
        <DrawerForm
          footer={
            <VdsButton
              variant="primary"
              size="medium"
              disabled={!canSubmit}
              isFullWidth
              onClick={() => handleSubmit(onSubmitInternal)()}
            >
              {t('SaveChanges')}
            </VdsButton>
          }
        >
          <>
            {errorMessage !== undefined && (
              <VdsNotification variant="inline" status="danger" showCloseButton={false}>
                <div slot="header">{t('Error')}</div>
                <span slot="body">{errorMessage}</span>
              </VdsNotification>
            )}

            <CasFormInput key="name" control={control} name="name" label={t('Fields.StyleName')} />
            <div className={styles.desc}>
              <CasFormInput
                key={'desc'}
                control={control}
                name="desc"
                label={t('Fields.Description')}
                element="textarea"
              />
            </div>
            <CasDivider />
            <FormColourConfigSelector control={control} watch={watch} setValue={setValue} />
            <CasDivider />
            <FormBorderConfigSelector control={control} watch={watch} setValue={setValue} />
          </>
        </DrawerForm>
      </DrawerPanel>

      {isLoaded && (
        <DevTool control={control} styles={{ panel: { height: '90vh' } }} placement="bottom-left" />
      )}
    </>
  );
}

export default ManageGeofenceStyle;
